import React from "react";
import { useDebounce } from "@tiny/components";
import { SegmentCalculation } from "./SegmentMeter.types";

/**
 * Debounced backend segment calculation — the same calculator as the launch
 * estimate, so composer and invoice agree. `calculate` is injected per host.
 */
export function useSegmentCalculation<T extends SegmentCalculation>(
  body: string,
  calculate: (body: string) => Promise<T>
) {
  const debouncedBody = useDebounce(body, 400);
  const [calculation, setCalculation] = React.useState<T>();
  const [loading, setLoading] = React.useState(false);
  const requestIdRef = React.useRef(0);
  const calculateRef = React.useRef(calculate);
  calculateRef.current = calculate;

  React.useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    calculateRef
      .current(debouncedBody)
      .then((result) => {
        // Drop an older, slower response landing after a newer one.
        if (requestId === requestIdRef.current) setCalculation(result);
      })
      .catch(() => {
        // Don't keep showing the previous body's count.
        if (requestId === requestIdRef.current) setCalculation(undefined);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [debouncedBody]);

  return { calculation, loading: loading || debouncedBody !== body };
}
